import { ValidationErrorsMap } from '@v2/states/validation';
import { AsyncLoadable } from '@v2/utils/mobx/async-loadable';
import { makeAutoObservable, reaction } from 'mobx';
import { diContainer } from 'shared/infrastructure/di-container';
import { ProviderClient, ProviderFactory } from 'shared/providers/provider';
import { DataField, DataObject } from 'shared/providers/types';
import { FilterCondition, ProviderType, PullMode } from 'shared/types/flowstep';

const getObjectTypeValidationMessage = (provider: ProviderType) => {
  switch (provider) {
    case ProviderType.HubspotList:
      return 'List must be selected';
    default:
      return 'Object type must be selected';
  }
};

export class PullSettingsState {
  createNewSheet = true;
  _selectedObject: DataObject | undefined = undefined;
  selectedFieldNames: string[] = [];
  conditions: FilterCondition[] = [];
  pullMode: PullMode = PullMode.OVERWRITE;
  withDateTimeColumn = false;
  private provider: ProviderClient;

  _selectedObjectId: string | undefined = undefined;
  objectLists = new AsyncLoadable<DataObject[]>([]);
  _objectFields = new AsyncLoadable<DataField[]>([]);

  private loadObjectFields() {
    this.objectFields.load(async () => {
      if (this._selectedObject) {
        return await this.provider.loadObjectFields(this._selectedObject.id || this._selectedObject.name);
      }
      return [];
    });
  }

  constructor(private providerType: ProviderType) {
    makeAutoObservable(this);

    const providerFactory = diContainer.resolve<ProviderFactory>('providerFactory');
    this.provider = providerFactory.getProviderClient(providerType);
    this.objectLists.load(async () => this.provider.loadObjectTypes());

    reaction(
      () => this.pullMode,
      (mode) => {
        if (mode !== PullMode.APPEND) {
          this.withDateTimeColumn = false;
        }
      },
    );
  }

  validate(errors: ValidationErrorsMap<string>): void {
    if (!this.selectedObject) {
      errors.set('object-type-not-selected', getObjectTypeValidationMessage(this.providerType));
    }

    if (this.selectedFieldNames.length === 0) {
      errors.set('selected-fields-empty', 'At least one field must be selected');
    }
  }

  get selectedObject() {
    if (!this._selectedObject && this._selectedObjectId && this.objectLists.isReady) {
      this._selectedObject = this.objectLists.data.find((object) => object.name === this._selectedObjectId);
      this.loadObjectFields();
    }

    return this._selectedObject;
  }

  set selectedObject(value: DataObject | undefined) {
    this._selectedObject = value;
    this.selectedFieldNames = [];
    this.loadObjectFields();
  }

  get objectLoadingError() {
    return this.objectLists.error;
  }

  get objectFieldsLoadingError() {
    return this._objectFields.error;
  }

  get objectFields() {
    return this._objectFields;
  }

  set selectedObjectId(id: string) {
    this._selectedObjectId = id;
  }

  get selectedObjectId(): string | undefined {
    return this._selectedObject ? this._selectedObject.name : this._selectedObjectId;
  }
}
